/**
 * PointAddressEditor: one point's address and data type for a non-Modbus
 * driver (web brief U6), rendered from the driver's address_schema with the
 * compact SchemaForm, and the type picked from the driver's `data_types`.
 * Modbus rows keep their own register editor (space / word order / scale).
 */

import { useId } from "react";
import { NativeSelect } from "@/lib/ui/native-select";
import type { DriverInfo, PointDataType } from "@/lib/api";
import { POINT_DATA_TYPES } from "@/lib/iot/points";
import { formToParams, paramsToForm, validateParams } from "@/lib/iot/schema-form";
import { SchemaForm } from "@/components/shared/SchemaForm";

export interface PointAddressEditorProps {
  driver: DriverInfo;
  address: Record<string, unknown> | undefined;
  dataType: PointDataType | undefined;
  onChange: (next: { address: Record<string, unknown>; dataType: PointDataType }) => void;
  /** Server problems for this point (a 422's params[i] fields), by field; "data_type" for the type. */
  serverErrors?: Record<string, string>;
  /** Accessible name prefix, e.g. "Row 2". */
  ariaPrefix?: string;
}

export function PointAddressEditor({
  driver,
  address,
  dataType,
  onChange,
  serverErrors,
  ariaPrefix,
}: PointAddressEditorProps) {
  const typeId = useId();
  const types = driver.data_types?.length ? driver.data_types : POINT_DATA_TYPES;
  const type = dataType && types.includes(dataType) ? dataType : types.includes("int16") ? "int16" : types[0];
  const values = paramsToForm(driver.address_schema, address ?? {});
  const check = validateParams(driver.address_schema, address ?? {});
  const touched = !!address && Object.keys(address).length > 0;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-2">
      <SchemaForm
        schema={driver.address_schema}
        values={values}
        onChange={(next) => onChange({ address: formToParams(driver.address_schema, next), dataType: type })}
        errors={{ ...(touched ? check.errors : {}), ...(serverErrors ?? {}) }}
        compact
        ariaPrefix={ariaPrefix}
      />
      <div className="flex items-center gap-2">
        <label htmlFor={typeId} className="text-[11px] font-medium text-on-surface">
          Read as
        </label>
        <NativeSelect
          id={typeId}
          className="h-8 w-32 text-xs"
          value={type}
          onChange={(e) => onChange({ address: address ?? {}, dataType: e.target.value as PointDataType })}
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </NativeSelect>
        {serverErrors?.data_type && <span className="text-[11px] text-danger">{serverErrors.data_type}</span>}
      </div>
    </div>
  );
}
